import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { useMutation } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  runAiBenchmark,
  type BenchmarkRun,
  type BenchmarkSummary,
} from "@/lib/admin.ai-benchmark.functions";

export const Route = createFileRoute("/admin/ai-benchmark")({
  head: () => ({
    meta: [
      { title: "AI benchmark — Tiger's Eye Admin" },
      { name: "robots", content: "noindex,nofollow" },
    ],
  }),
  component: AiBenchmark,
});

function AiBenchmark() {
  const runFn = useServerFn(runAiBenchmark);

  const [iterations, setIterations] = useState(3);
  const [lovableModel, setLovableModel] = useState("google/gemini-2.5-flash");
  const [xaiModel, setXaiModel] = useState("grok-4-latest");
  const [includeLovable, setIncludeLovable] = useState(true);
  const [includeXai, setIncludeXai] = useState(true);
  const [systemPrompt, setSystemPrompt] = useState("");
  const [userPrompt, setUserPrompt] = useState("");

  const mut = useMutation({
    mutationFn: () =>
      runFn({
        data: {
          iterations,
          lovable_model: lovableModel.trim(),
          xai_model: xaiModel.trim(),
          providers: [
            ...(includeLovable ? (["lovable"] as const) : []),
            ...(includeXai ? (["xai"] as const) : []),
          ],
          system: systemPrompt.trim() || undefined,
          user: userPrompt.trim() || undefined,
        },
      }),
    onError: (err) => toast.error(err instanceof Error ? err.message : "Run failed"),
  });

  const summaries = mut.data?.summaries ?? [];
  const runs = mut.data?.runs ?? [];

  return (
    <div className="space-y-8 max-w-5xl">
      <div>
        <p className="overline">Test environment</p>
        <h2 className="font-serif text-3xl text-primary mt-1">AI benchmark</h2>
        <p className="text-sm text-ink-secondary mt-2">
          Fire N identical chat-completion requests at each selected provider
          and measure end-to-end latency from the server. This bypasses the
          live <span className="font-mono">/admin/ai-provider</span> toggle so
          you can compare without disturbing production traffic. Times are
          measured server-side around <span className="font-mono">fetch()</span> —
          they include the gateway hop but exclude client network time.
        </p>
      </div>

      <form
        className="space-y-5 border border-border bg-card p-6"
        onSubmit={(e) => {
          e.preventDefault();
          if (!includeLovable && !includeXai) {
            toast.error("Pick at least one provider.");
            return;
          }
          mut.mutate();
        }}
      >
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <label className="block text-sm">
            <span className="block font-medium text-ink-secondary mb-1">
              Iterations per provider
            </span>
            <input
              type="number"
              min={1}
              max={10}
              value={iterations}
              onChange={(e) =>
                setIterations(Math.max(1, Math.min(10, Number(e.target.value) || 1)))
              }
              className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm font-mono"
            />
          </label>
          <label className="block text-sm md:col-span-1">
            <span className="block font-medium text-ink-secondary mb-1">
              Lovable AI model
            </span>
            <input
              type="text"
              value={lovableModel}
              onChange={(e) => setLovableModel(e.target.value)}
              disabled={!includeLovable}
              className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm font-mono disabled:opacity-50"
            />
          </label>
          <label className="block text-sm md:col-span-1">
            <span className="block font-medium text-ink-secondary mb-1">
              x.ai model
            </span>
            <input
              type="text"
              value={xaiModel}
              onChange={(e) => setXaiModel(e.target.value)}
              disabled={!includeXai}
              className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm font-mono disabled:opacity-50"
            />
          </label>
        </div>

        <div className="flex gap-6 text-sm">
          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={includeLovable}
              onChange={(e) => setIncludeLovable(e.target.checked)}
            />
            <span>Lovable AI</span>
          </label>
          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={includeXai}
              onChange={(e) => setIncludeXai(e.target.checked)}
            />
            <span>x.ai (Grok)</span>
          </label>
        </div>

        <details className="text-sm">
          <summary className="cursor-pointer text-ink-tertiary">
            Custom prompts (optional)
          </summary>
          <div className="mt-3 space-y-3">
            <label className="block">
              <span className="block font-medium text-ink-secondary mb-1">
                System
              </span>
              <textarea
                rows={3}
                value={systemPrompt}
                onChange={(e) => setSystemPrompt(e.target.value)}
                placeholder="Default: tiny JSON probe."
                className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm font-mono"
              />
            </label>
            <label className="block">
              <span className="block font-medium text-ink-secondary mb-1">
                User
              </span>
              <textarea
                rows={3}
                value={userPrompt}
                onChange={(e) => setUserPrompt(e.target.value)}
                placeholder="Use {{TIMESTAMP}} to avoid trivial caching."
                className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm font-mono"
              />
            </label>
          </div>
        </details>

        <div className="flex items-center gap-3 pt-2 border-t border-border">
          <button
            type="submit"
            disabled={mut.isPending}
            className="px-4 py-2 bg-primary text-primary-foreground text-sm font-medium hover:opacity-90 disabled:opacity-50"
          >
            {mut.isPending ? "Running…" : "Run benchmark"}
          </button>
          {mut.data && (
            <span className="text-xs text-ink-tertiary font-mono">
              Last run {new Date(mut.data.ranAt).toLocaleTimeString()}
            </span>
          )}
        </div>
      </form>

      {summaries.length > 0 && <SummaryTable summaries={summaries} />}
      {runs.length > 0 && <RunsTable runs={runs} />}
    </div>
  );
}

function SummaryTable({ summaries }: { summaries: BenchmarkSummary[] }) {
  return (
    <section className="space-y-3">
      <h3 className="overline">Summary</h3>
      <div className="overflow-x-auto border border-border">
        <table className="w-full text-sm">
          <thead className="bg-muted/40 text-left">
            <tr>
              <th className="px-3 py-2">Provider</th>
              <th className="px-3 py-2">Model</th>
              <th className="px-3 py-2 text-right">OK / Runs</th>
              <th className="px-3 py-2 text-right">Avg total</th>
              <th className="px-3 py-2 text-right">p50</th>
              <th className="px-3 py-2 text-right">Min / Max</th>
              <th className="px-3 py-2 text-right">Avg TTFB</th>
              <th className="px-3 py-2 text-right">Avg read</th>
              <th className="px-3 py-2 text-right">Avg tokens</th>
            </tr>
          </thead>
          <tbody>
            {summaries.map((s) => (
              <tr key={`${s.provider}-${s.model}`} className="border-t border-border">
                <td className="px-3 py-2 font-medium">{s.provider}</td>
                <td className="px-3 py-2 font-mono text-xs">{s.model}</td>
                <td className="px-3 py-2 text-right">
                  {s.ok} / {s.runs}
                </td>
                <td className="px-3 py-2 text-right font-mono">{s.avgTotalMs} ms</td>
                <td className="px-3 py-2 text-right font-mono">{s.p50TotalMs} ms</td>
                <td className="px-3 py-2 text-right font-mono">
                  {s.minTotalMs} / {s.maxTotalMs}
                </td>
                <td className="px-3 py-2 text-right font-mono">{s.avgTtfbMs} ms</td>
                <td className="px-3 py-2 text-right font-mono">{s.avgReadMs} ms</td>
                <td className="px-3 py-2 text-right font-mono">{s.avgTokens}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function RunsTable({ runs }: { runs: BenchmarkRun[] }) {
  return (
    <section className="space-y-3">
      <h3 className="overline">Per-call detail</h3>
      <div className="overflow-x-auto border border-border">
        <table className="w-full text-sm">
          <thead className="bg-muted/40 text-left">
            <tr>
              <th className="px-3 py-2">#</th>
              <th className="px-3 py-2">Provider</th>
              <th className="px-3 py-2 text-right">Total</th>
              <th className="px-3 py-2 text-right">TTFB</th>
              <th className="px-3 py-2 text-right">Read</th>
              <th className="px-3 py-2 text-right">Tokens</th>
              <th className="px-3 py-2 text-right">Completion chars</th>
              <th className="px-3 py-2">Notes</th>
            </tr>
          </thead>
          <tbody>
            {runs.map((r, idx) => (
              <tr key={idx} className="border-t border-border">
                <td className="px-3 py-2 font-mono text-xs">{r.iteration}</td>
                <td className="px-3 py-2">{r.provider}</td>
                <td className="px-3 py-2 text-right font-mono">
                  {r.timing ? `${r.timing.totalMs} ms` : "—"}
                </td>
                <td className="px-3 py-2 text-right font-mono">
                  {r.timing ? `${r.timing.ttfbMs} ms` : "—"}
                </td>
                <td className="px-3 py-2 text-right font-mono">
                  {r.timing ? `${r.timing.readMs} ms` : "—"}
                </td>
                <td className="px-3 py-2 text-right font-mono">
                  {r.timing?.tokensUsed ?? "—"}
                </td>
                <td className="px-3 py-2 text-right font-mono">
                  {r.timing?.completionChars ?? "—"}
                </td>
                <td className="px-3 py-2 text-xs text-ink-tertiary">
                  {r.ok ? (
                    <span className="font-mono">
                      {r.completionPreview?.slice(0, 80) ?? ""}
                    </span>
                  ) : (
                    <span className="text-destructive">{r.error}</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}
